"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { api, ApiError, TokenOut } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge, Button, Card } from "@/components/ui";

const TEST_ACCOUNTS = [
  { phone: "+79990000001", label: "Раханский Виталий", role: "тренер" },
  { phone: "+79990000002", label: "Козлов Михаил", role: "тренер" },
  { phone: "+79990000003", label: "Родион Синицын", role: "тренер" },
  { phone: "+79990001001", label: "Анна Крылова", role: "ученик" },
  { phone: "+79990001002", label: "Дмитрий Мельник", role: "ученик" },
  { phone: "+79990001003", label: "Вера Тихонова", role: "ученик" },
  { phone: "+79990000000", label: "Илья Ветров", role: "админ" },
];

export default function LoginPage() {
  const [phone, setPhone] = useState("+7");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [confirm18, setConfirm18] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [devBusyPhone, setDevBusyPhone] = useState<string | null>(null);
  const { login } = useAuth();
  const router = useRouter();

  const requestOtp = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/phone/request-otp", { phone });
      setStep("code");
    } catch (e) {
      setError(e instanceof ApiError ? String(e.detail) : "Не удалось отправить код");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<TokenOut>("/auth/phone/verify-otp", {
        phone,
        code,
        confirm_18_plus: confirm18,
      });
      await login(res.access_token);
      router.push("/");
    } catch (e) {
      setError(e instanceof ApiError ? String(e.detail) : "Не удалось подтвердить код");
    } finally {
      setBusy(false);
    }
  };

  const devLogin = async (accountPhone: string) => {
    setError(null);
    setDevBusyPhone(accountPhone);
    try {
      const res = await api.post<TokenOut>("/auth/phone/dev-login", { phone: accountPhone });
      await login(res.access_token);
      router.push("/");
    } catch (e) {
      setError(e instanceof ApiError ? String(e.detail) : "Дев-вход недоступен");
    } finally {
      setDevBusyPhone(null);
    }
  };

  return (
    <div className="mx-auto max-w-sm space-y-8">
      <div>
        <h1 className="mb-6 font-display text-xl font-bold text-text">Вход по телефону</h1>

        {step === "phone" && (
          <div className="space-y-4">
            <label className="block text-sm text-text-muted">
              Номер телефона
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-text"
                placeholder="+79001234567"
              />
            </label>
            {error && <p className="text-sm text-risk">{error}</p>}
            <Button onClick={requestOtp} disabled={busy} className="w-full">
              Получить код
            </Button>
          </div>
        )}

        {step === "code" && (
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Код отправлен на {phone}. В dev-режиме он выводится в консоль backend-сервера (ConsoleSmsSender).
            </p>
            <label className="block text-sm text-text-muted">
              Код из SMS
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-text"
                placeholder="123456"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-text-muted">
              <input type="checkbox" checked={confirm18} onChange={(e) => setConfirm18(e.target.checked)} />
              Мне есть 18 лет (обязательно при первом входе)
            </label>
            {error && <p className="text-sm text-risk">{error}</p>}
            <Button onClick={verifyOtp} disabled={busy} className="w-full">
              Войти
            </Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Тестовые аккаунты</span>
          <Badge variant="neutral">только для пилота</Badge>
        </div>
        <div className="space-y-1.5">
          {TEST_ACCOUNTS.map((acc) => (
            <button
              key={acc.phone}
              onClick={() => devLogin(acc.phone)}
              disabled={devBusyPhone !== null}
              className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-sm transition-colors hover:border-brand/50 disabled:opacity-50"
            >
              <span className="text-text">{acc.label}</span>
              <Badge variant="neutral">{devBusyPhone === acc.phone ? "…" : acc.role}</Badge>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
